import type { WindowSuggestionRecord } from "@corklog/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { locale } from "../locale.js";
import { AiNotice } from "./AiNotice.js";
import { AnswerGrounds } from "./AnswerGrounds.js";

/**
 * What was said about this wine's years, and when.
 *
 * **The years are on the wine; the argument that produced them is only here.**
 * *This is appassimento in general, I do not know the producer* and *2017 was
 * warm and comes forward early* are the difference between a number somebody
 * can weigh and a number they can only believe or disbelieve — and in five
 * years nobody will remember which of the two they were looking at.
 *
 * Under the band and above everything else about the wine, because it belongs
 * to the years rather than to the bottles. Prose in the reading colour, not the
 * muted one: it is meant to be read, unlike the provenance line above it, which
 * is meant to be glanced at.
 *
 * **Dated and attributed**, for the reason `WineRating` carries who said a
 * score and on what scale. In two years the models will be better, and a year
 * that came from this one should say so rather than pass as current.
 *
 * The newest is open; the rest are behind one line. A wine asked about three
 * times has three arguments worth keeping and one worth reading first —
 * including the ones that were declined, which are sometimes the more
 * interesting: *it said 2032 and I said no* is a judgement too.
 */
const YEARS = ["drinkFrom", "peakFrom", "peakTo", "drinkBy"] as const;

function Reason({ suggestion }: { suggestion: WindowSuggestionRecord }) {
  const { t } = useTranslation();
  const taken = YEARS.filter((year) => suggestion.accepted[year]);
  const proposed = YEARS.filter((year) => suggestion[year] !== null);

  return (
    <li className="border-line border-t py-3 first:border-t-0">
      <p className="max-w-prose text-sm">{suggestion.reason}</p>
      <AnswerGrounds
        labels={suggestion.basis.map((basis) => t(`window.basis.${basis}`))}
        told={suggestion.told}
      />
      <p className="text-muted mt-1 text-xs">
        {t("window.reasons.attribution", {
          model: suggestion.model,
          date: new Date(suggestion.askedAt).toLocaleDateString(locale()),
        })}
        {" · "}
        {t("window.reasons.proposed", {
          years: proposed
            .map((year) =>
              t("window.reasons.year", {
                field: t(`wines.${year}`).toLocaleLowerCase("sv-SE"),
                year: suggestion[year],
              }),
            )
            .join(" · "),
        })}
        {" · "}
        {/* Three outcomes and not two. Nothing taken is a decision, and a
            suggestion whose years were edited before being saved is neither
            "taken" nor "declined" — the numbers standing on the wine are then
            the owner's, and this says so rather than claiming credit. */}
        {taken.length === proposed.length && proposed.length > 0
          ? t("window.reasons.tookAll")
          : taken.length > 0
            ? t("window.reasons.tookSome", { count: taken.length })
            : suggestion.actedAt === null
              ? t("window.reasons.notActed")
              : t("window.reasons.tookNoneUnchanged")}
      </p>
    </li>
  );
}

export function WindowReasons({
  suggestions,
}: {
  suggestions: readonly WindowSuggestionRecord[];
}) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  if (suggestions.length === 0) return null;

  const [newest, ...earlier] = suggestions;
  if (newest === undefined) return null;

  return (
    <section className="mt-4">
      {/* Once for the list: every reason in it is the model's. */}
      <AiNotice className="mb-1" />
      <ul>
        <Reason suggestion={newest} />
        {showAll && earlier.map((one) => <Reason key={one.id} suggestion={one} />)}
      </ul>
      {earlier.length > 0 && !showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="text-accent mt-1 text-sm underline-offset-4 hover:underline"
        >
          {t("window.reasons.earlier", { count: earlier.length })}
        </button>
      )}
    </section>
  );
}
